import { useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../../api/client";
import { listDemoDmas } from "../../api/mock";
import { useAuth } from "../../auth/AuthContext";
import { PageHeader } from "../../layout/PageHeader";
import { Icon } from "../../components/Icon";

interface ConsumoSeriePunto {
  ts: string;
  volumen_m3: string;
}

interface DashboardResponse {
  dma_id: string;
  periodo_id: string;
  total_m3: string;
  clientes_activos: number;
  serie: ConsumoSeriePunto[];
}

type ChartKind = "area" | "bar" | "line";
type SortDir = "asc" | "desc";

const PERIODOS = [
  "2025-01",
  "2024-12",
  "2024-11",
  "2024-10",
  "2024-09",
  "2024-08",
];

const CHART_OPTIONS: Array<{
  value: ChartKind;
  label: string;
  iconName: "dashboard" | "report";
}> = [
  { value: "area", label: "Área", iconName: "dashboard" },
  { value: "bar", label: "Barras", iconName: "report" },
  { value: "line", label: "Línea", iconName: "dashboard" },
];

function formatNumber(value: number | string, fractionDigits = 0): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("es-CL", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function previousYearPeriodo(periodoId: string): string {
  const [yearStr, monthStr] = periodoId.split("-");
  const year = Number(yearStr);
  if (!Number.isFinite(year)) return periodoId;
  return `${year - 1}-${monthStr ?? "01"}`;
}

function downloadCsv(filename: string, rows: Array<Record<string, string>>): void {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: string) =>
    /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  const body = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h] ?? "")).join(",")),
  ].join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function OperadorDashboards(): JSX.Element {
  const { isDemo } = useAuth();
  const dmas = listDemoDmas();
  const defaultDma = dmas[0]?.dma_id ?? "";
  const [dmaId, setDmaId] = useState<string>(defaultDma);
  const [periodoId, setPeriodoId] = useState<string>(PERIODOS[0]);
  const [estado, setEstado] = useState<string>("");
  const [chartKind, setChartKind] = useState<ChartKind>("area");
  const [compareYoy, setCompareYoy] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<"date" | "volume">("date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState<number>(1);
  const pageSize = 7;

  const enabled = Boolean(dmaId) && isDemo;

  const currentQuery = useQuery<DashboardResponse>({
    queryKey: ["dashboards.dma", dmaId, periodoId, estado, "current"],
    queryFn: () =>
      api<DashboardResponse>(
        `/dashboards/dma/${dmaId}/consumo?periodo_id=${encodeURIComponent(
          periodoId,
        )}${estado ? `&estado=${encodeURIComponent(estado)}` : ""}`,
      ),
    enabled,
    staleTime: 60_000,
  });

  const previousPeriodo = previousYearPeriodo(periodoId);
  const yoyQuery = useQuery<DashboardResponse>({
    queryKey: ["dashboards.dma", dmaId, previousPeriodo, estado, "yoy"],
    queryFn: () =>
      api<DashboardResponse>(
        `/dashboards/dma/${dmaId}/consumo?periodo_id=${encodeURIComponent(
          previousPeriodo,
        )}${estado ? `&estado=${encodeURIComponent(estado)}` : ""}`,
      ),
    enabled: enabled && compareYoy,
    staleTime: 60_000,
  });

  // Pre-fetch every DMA so the selector cards can show their own totals.
  const dmaSummaries = useQueries({
    queries: dmas.map((dma) => ({
      queryKey: ["dashboards.dma", dma.dma_id, periodoId, "summary"],
      queryFn: () =>
        api<DashboardResponse>(
          `/dashboards/dma/${dma.dma_id}/consumo?periodo_id=${encodeURIComponent(
            periodoId,
          )}`,
        ),
      enabled: isDemo,
      staleTime: 60_000,
    })),
  });

  const chartData = useMemo(() => {
    if (!currentQuery.data) return [];
    const yoy = yoyQuery.data;
    const pickYoy = (idx: number): number | null => {
      if (!yoy) return null;
      const point = yoy.serie[idx];
      return point ? Number(point.volumen_m3) : null;
    };
    return currentQuery.data.serie.map((p, idx) => ({
      day: p.ts.slice(8, 10),
      ts: p.ts,
      actual: Number(p.volumen_m3),
      anterior: pickYoy(idx),
    }));
  }, [currentQuery.data, yoyQuery.data]);

  const promedio = useMemo(() => {
    if (chartData.length === 0) return 0;
    const total = chartData.reduce((acc, p) => acc + p.actual, 0);
    return total / chartData.length;
  }, [chartData]);

  const totalActual = currentQuery.data
    ? Number(currentQuery.data.total_m3)
    : 0;
  const totalAnterior = yoyQuery.data
    ? Number(yoyQuery.data.total_m3)
    : null;
  const yoyDelta =
    totalAnterior !== null && totalAnterior > 0
      ? ((totalActual - totalAnterior) / totalAnterior) * 100
      : null;

  const sortedRows = useMemo(() => {
    if (!currentQuery.data) return [];
    const rows = currentQuery.data.serie.map((p, idx) => ({
      idx,
      date: p.ts.slice(0, 10),
      volumen: Number(p.volumen_m3),
      anterior:
        yoyQuery.data?.serie[idx]?.volumen_m3
          ? Number(yoyQuery.data.serie[idx].volumen_m3)
          : null,
    }));
    rows.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortBy === "date") {
        return a.date.localeCompare(b.date) * dir;
      }
      return (a.volumen - b.volumen) * dir;
    });
    return rows;
  }, [currentQuery.data, yoyQuery.data, sortBy, sortDir]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedRows.length / pageSize),
  );
  const safePage = Math.min(page, totalPages);
  const visibleRows = sortedRows.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  function toggleSort(field: "date" | "volume"): void {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(1);
  }

  function handleExport(): void {
    if (!currentQuery.data) return;
    downloadCsv(
      `dashboard-${dmaId}-${periodoId}.csv`,
      sortedRows.map((r) => ({
        fecha: r.date,
        volumen_m3: r.volumen.toFixed(3),
        volumen_anio_anterior_m3:
          r.anterior !== null ? r.anterior.toFixed(3) : "",
      })),
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Operador · R3.*"
        title="Dashboards de consumo"
        subtitle="Selecciona una DMA, compara contra el año anterior y descarga la serie."
        actions={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleExport}
              disabled={!currentQuery.data}
            >
              <Icon name="report" size={14} /> Exportar CSV
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => currentQuery.refetch()}
              disabled={!enabled}
            >
              Actualizar
            </button>
          </>
        }
      />

      <section className="card" style={{ marginBottom: 24 }}>
        <div className="card__title">
          <span>DMA</span>
          <span className="text-muted" style={{ fontWeight: 400 }}>
            {dmas.length} disponibles
          </span>
        </div>
        <div className="dma-grid">
          {dmas.map((dma, idx) => {
            const summary = dmaSummaries[idx]?.data;
            const total = summary ? Number(summary.total_m3) : null;
            return (
              <button
                key={dma.dma_id}
                type="button"
                className="dma-chip"
                aria-pressed={dma.dma_id === dmaId}
                onClick={() => setDmaId(dma.dma_id)}
              >
                <span className="dma-chip__name">
                  {dma.codigo}
                  <span className="text-muted" style={{ fontWeight: 400 }}>
                    {" "}
                    · {dma.nombre}
                  </span>
                </span>
                <span className="dma-chip__meta">
                  {total !== null
                    ? `${formatNumber(total)} m³ · ${formatNumber(
                        dma.clientes,
                      )} clientes`
                    : "Cargando…"}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="card" style={{ marginBottom: 24 }}>
        <div className="card__title">
          <span>Filtros</span>
          <label className="toggle">
            <input
              type="checkbox"
              checked={compareYoy}
              onChange={(e) => setCompareYoy(e.target.checked)}
            />
            Comparar con {previousPeriodo}
          </label>
        </div>
        <div className="form-grid">
          <label className="form-field">
            Periodo
            <select
              value={periodoId}
              onChange={(e) => {
                setPeriodoId(e.target.value);
                setPage(1);
              }}
            >
              {PERIODOS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            Estado del servicio
            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            >
              <option value="">(todos)</option>
              <option value="activo">activo</option>
              <option value="suspendido">suspendido</option>
              <option value="cortado">cortado</option>
            </select>
          </label>
          <div className="form-field">
            <span>Tipo de gráfico</span>
            <div className="segmented" role="tablist" aria-label="Tipo de gráfico">
              {CHART_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className="segmented__btn"
                  aria-pressed={chartKind === opt.value}
                  onClick={() => setChartKind(opt.value)}
                >
                  <Icon name={opt.iconName} size={12} /> {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {!enabled && (
        <div className="status-block">
          Selecciona una DMA para visualizar el dashboard.
        </div>
      )}

      {currentQuery.isLoading && enabled && (
        <div className="status-block status-block--info">Cargando datos…</div>
      )}

      {currentQuery.error && (
        <div className="status-block status-block--alert">
          Error al cargar el dashboard: {String(currentQuery.error)}
        </div>
      )}

      {currentQuery.data && (
        <>
          <div className="card-grid" style={{ marginBottom: 24 }}>
            <div className="kpi">
              <span className="kpi__label">Total m³ del periodo</span>
              <span className="kpi__value">{formatNumber(totalActual)}</span>
              <span className="kpi__hint">
                {dmas.find((d) => d.dma_id === dmaId)?.codigo ?? "DMA"} ·{" "}
                {periodoId}
                {yoyDelta !== null && (
                  <span
                    className={`kpi__delta kpi__delta--${
                      yoyDelta > 0
                        ? "up"
                        : yoyDelta < 0
                          ? "down"
                          : "neutral"
                    }`}
                  >
                    {yoyDelta > 0 ? "▲" : yoyDelta < 0 ? "▼" : "—"}{" "}
                    {formatNumber(Math.abs(yoyDelta), 1)} %
                  </span>
                )}
              </span>
            </div>
            <div className="kpi kpi--accent">
              <span className="kpi__label">Clientes activos</span>
              <span className="kpi__value">
                {formatNumber(currentQuery.data.clientes_activos)}
              </span>
              <span className="kpi__hint">en la DMA seleccionada</span>
            </div>
            <div className="kpi kpi--info">
              <span className="kpi__label">Promedio diario</span>
              <span className="kpi__value">
                {formatNumber(promedio, 1)} m³
              </span>
              <span className="kpi__hint">
                {chartData.length} días observados
              </span>
            </div>
            <div className="kpi kpi--warn">
              <span className="kpi__label">Año anterior</span>
              <span className="kpi__value">
                {totalAnterior !== null
                  ? `${formatNumber(totalAnterior)} m³`
                  : "—"}
              </span>
              <span className="kpi__hint">
                Periodo {previousPeriodo}
                {!compareYoy && " (activa el comparativo)"}
              </span>
            </div>
          </div>

          <section className="card" style={{ marginBottom: 24 }}>
            <div className="card__title">
              <span>Serie diaria</span>
              <span className="text-muted" style={{ fontWeight: 400 }}>
                {chartData.length} puntos · arrastra el selector inferior para
                hacer zoom
              </span>
            </div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                {chartKind === "area" ? (
                  <AreaChart
                    data={chartData}
                    margin={{ top: 8, right: 12, bottom: 8, left: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="grad-actual"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="5%" stopColor="#1391c4" stopOpacity={0.5} />
                        <stop
                          offset="95%"
                          stopColor="#1391c4"
                          stopOpacity={0.04}
                        />
                      </linearGradient>
                      <linearGradient
                        id="grad-anterior"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="5%" stopColor="#5c6ad1" stopOpacity={0.32} />
                        <stop
                          offset="95%"
                          stopColor="#5c6ad1"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#e3edf4" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: "#506072" }}
                      stroke="#c2ccda"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#506072" }}
                      stroke="#c2ccda"
                      width={60}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #e1e7ef",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value: number) =>
                        [`${formatNumber(value, 1)} m³`, ""]
                      }
                      labelFormatter={(d) => `Día ${d}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                      type="monotone"
                      dataKey="actual"
                      name={`Actual (${periodoId})`}
                      stroke="#0a5d82"
                      strokeWidth={2}
                      fill="url(#grad-actual)"
                    />
                    {compareYoy && (
                      <Area
                        type="monotone"
                        dataKey="anterior"
                        name={`Anterior (${previousPeriodo})`}
                        stroke="#4452b6"
                        strokeWidth={2}
                        fill="url(#grad-anterior)"
                        strokeDasharray="4 4"
                      />
                    )}
                    <Brush
                      dataKey="day"
                      height={26}
                      stroke="#94d2eb"
                      travellerWidth={8}
                    />
                  </AreaChart>
                ) : chartKind === "bar" ? (
                  <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 12, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid stroke="#e3edf4" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: "#506072" }}
                      stroke="#c2ccda"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#506072" }}
                      stroke="#c2ccda"
                      width={60}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #e1e7ef",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value: number) =>
                        [`${formatNumber(value, 1)} m³`, ""]
                      }
                      labelFormatter={(d) => `Día ${d}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="actual"
                      name={`Actual (${periodoId})`}
                      fill="#1391c4"
                      radius={[6, 6, 0, 0]}
                    />
                    {compareYoy && (
                      <Bar
                        dataKey="anterior"
                        name={`Anterior (${previousPeriodo})`}
                        fill="#94a0e8"
                        radius={[6, 6, 0, 0]}
                      />
                    )}
                    <Brush
                      dataKey="day"
                      height={26}
                      stroke="#94d2eb"
                      travellerWidth={8}
                    />
                  </BarChart>
                ) : (
                  <LineChart
                    data={chartData}
                    margin={{ top: 8, right: 12, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid stroke="#e3edf4" vertical={false} />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: "#506072" }}
                      stroke="#c2ccda"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#506072" }}
                      stroke="#c2ccda"
                      width={60}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #e1e7ef",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value: number) =>
                        [`${formatNumber(value, 1)} m³`, ""]
                      }
                      labelFormatter={(d) => `Día ${d}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      name={`Actual (${periodoId})`}
                      stroke="#0a5d82"
                      strokeWidth={2.5}
                      dot={false}
                    />
                    {compareYoy && (
                      <Line
                        type="monotone"
                        dataKey="anterior"
                        name={`Anterior (${previousPeriodo})`}
                        stroke="#5c6ad1"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        dot={false}
                      />
                    )}
                    <Brush
                      dataKey="day"
                      height={26}
                      stroke="#94d2eb"
                      travellerWidth={8}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card">
            <div className="card__title">
              <span>Detalle por día</span>
              <span className="text-muted" style={{ fontWeight: 400 }}>
                {sortedRows.length} filas · página {safePage}/{totalPages}
              </span>
            </div>
            <div className="table-wrapper">
              <div className="table-toolbar">
                <span>
                  Orden:&nbsp;
                  <strong>{sortBy === "date" ? "fecha" : "volumen"}</strong>{" "}
                  {sortDir === "asc" ? "↑" : "↓"}
                </span>
                <span className="flex-spacer" />
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={safePage >= totalPages}
                >
                  Siguiente
                </button>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th
                      className="sortable"
                      onClick={() => toggleSort("date")}
                    >
                      Fecha {sortBy === "date" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th
                      className="numeric sortable"
                      onClick={() => toggleSort("volume")}
                    >
                      Volumen m³{" "}
                      {sortBy === "volume" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </th>
                    {compareYoy && (
                      <th className="numeric">Año anterior m³</th>
                    )}
                    {compareYoy && <th className="numeric">Δ %</th>}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const delta =
                      compareYoy && row.anterior !== null && row.anterior > 0
                        ? ((row.volumen - row.anterior) / row.anterior) * 100
                        : null;
                    return (
                      <tr key={row.idx}>
                        <td className="text-mono">{row.date}</td>
                        <td className="numeric">
                          {formatNumber(row.volumen, 3)}
                        </td>
                        {compareYoy && (
                          <td className="numeric">
                            {row.anterior !== null
                              ? formatNumber(row.anterior, 3)
                              : "—"}
                          </td>
                        )}
                        {compareYoy && (
                          <td className="numeric">
                            {delta !== null ? (
                              <span
                                className={`badge badge--${
                                  delta > 0
                                    ? "warn"
                                    : delta < 0
                                      ? "accent"
                                      : "neutral"
                                }`}
                              >
                                {delta > 0 ? "+" : ""}
                                {formatNumber(delta, 1)} %
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
